import { DiagnosticFormData } from '../../../types/diagnostic';
import { motion } from 'framer-motion';
import securityWeights from '../../../config/securityWeights.json';
import { dpsTheme } from '../../../styles/dpsTheme';
import { useDeviceSize } from '../../../hooks/useMediaQuery';

interface SecurityEquipmentProps {
  formData: DiagnosticFormData;
  updateFormData: (data: Partial<DiagnosticFormData>) => void;
}

type SecurityEquipment = {
  id: keyof typeof securityWeights.equipment;
  label: string;
  description: string;
  categories: string[];
};

const SECURITY_ITEMS: SecurityEquipment[] = Object.entries(securityWeights.equipment).map(([id, data]) => ({
  id: id as keyof typeof securityWeights.equipment,
  label: data.label,
  description: data.description,
  categories: data.categories
}));

const CATEGORIES: Record<string, { label: string; bgColor: string; textColor: string; borderColor: string }> = {
  protection: {
    label: 'Protection',
    bgColor: dpsTheme.colors.categories.protection.bg,
    textColor: dpsTheme.colors.categories.protection.primary,
    borderColor: dpsTheme.colors.categories.protection.border
  },
  surveillance: {
    label: 'Surveillance',
    bgColor: dpsTheme.colors.categories.surveillance.bg,
    textColor: dpsTheme.colors.categories.surveillance.primary,
    borderColor: dpsTheme.colors.categories.surveillance.border
  },
  deterrence: {
    label: 'Dissuasion',
    bgColor: dpsTheme.colors.categories.dissuasion.bg,
    textColor: dpsTheme.colors.categories.dissuasion.primary,
    borderColor: dpsTheme.colors.categories.dissuasion.border
  }
};

export default function SecurityEquipment({ formData, updateFormData }: SecurityEquipmentProps) {
  const { isMobile } = useDeviceSize();

  const handleEquipmentChange = (id: string, checked: boolean) => {
    const newEquipment = checked
      ? [...formData.equipment, id]
      : formData.equipment.filter(item => item !== id);

    updateFormData({ equipment: newEquipment });
  };

  return (
    <div>
      <h2 style={{
        fontSize: dpsTheme.typography.fontSize['3xl'],
        fontWeight: dpsTheme.typography.fontWeight.bold,
        color: dpsTheme.colors.text.primary,
        marginBottom: dpsTheme.spacing['2xl']
      }}>
        Équipements de sécurité actuels
      </h2>

      <div style={{ marginBottom: dpsTheme.spacing.xl, display: 'flex', flexWrap: 'wrap', gap: dpsTheme.spacing.md }}>
        {Object.entries(CATEGORIES).map(([key, { label, bgColor, textColor, borderColor }]) => (
          <span key={key} style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: `${dpsTheme.spacing.sm} ${dpsTheme.spacing.md}`,
            borderRadius: dpsTheme.borderRadius.full,
            fontSize: dpsTheme.typography.fontSize.sm,
            fontWeight: dpsTheme.typography.fontWeight.semibold,
            background: bgColor,
            color: textColor,
            border: `1px solid ${borderColor}`
          }}>
            {label}
          </span>
        ))}
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', gap: dpsTheme.spacing.lg }}>
        {SECURITY_ITEMS.map(({ id, label, description, categories }) => {
          const isSelected = formData.equipment.includes(id);
          return (
            <motion.div
              key={id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              whileHover={{ scale: 1.02 }}
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                padding: isMobile ? dpsTheme.spacing.md : dpsTheme.spacing.lg,
                background: isSelected ? dpsTheme.colors.background.cardHover : dpsTheme.colors.background.darker,
                backdropFilter: 'blur(10px)',
                borderRadius: dpsTheme.borderRadius.md,
                border: `2px solid ${isSelected ? dpsTheme.colors.accent.blue.primary : dpsTheme.colors.border.primary}`,
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: isSelected ? dpsTheme.shadows.button : 'none'
              }}
              onClick={() => handleEquipmentChange(id, !isSelected)}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: dpsTheme.spacing.md }}>
                <input
                  type="checkbox"
                  id={id}
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleEquipmentChange(id, e.target.checked);
                  }}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    accentColor: dpsTheme.colors.accent.blue.primary,
                    marginTop: '2px'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <label htmlFor={id} style={{
                    fontSize: dpsTheme.typography.fontSize.base,
                    fontWeight: dpsTheme.typography.fontWeight.semibold,
                    color: dpsTheme.colors.text.primary,
                    cursor: 'pointer',
                    display: 'block'
                  }}>
                    {label}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: dpsTheme.spacing.xs }}>
                    <p style={{
                      fontSize: dpsTheme.typography.fontSize.sm,
                      color: dpsTheme.colors.text.muted,
                      marginTop: dpsTheme.spacing.sm,
                      flex: 1
                    }}>
                      {description}
                    </p>
                    {id === 'doorSensor' && (
                      <div
                        title="Les détecteurs d'ouverture se placent sur les portes, fenêtres et volets. Ils alertent dès qu'un point d'accès est ouvert ou forcé, même si l'alarme n'est pas activée."
                        style={{
                          display: 'inline-flex',
                          cursor: 'help',
                          fontSize: dpsTheme.typography.fontSize.sm,
                          color: dpsTheme.colors.accent.blue.primary,
                          marginTop: dpsTheme.spacing.sm,
                          flexShrink: 0
                        }}
                      >
                        ℹ️
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: dpsTheme.spacing.sm, marginTop: dpsTheme.spacing.md }}>
                    {categories.map(category => {
                      const cat = CATEGORIES[category];
                      return (
                        <span
                          key={category}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: `4px ${dpsTheme.spacing.sm}`,
                            borderRadius: dpsTheme.borderRadius.full,
                            fontSize: dpsTheme.typography.fontSize.xs,
                            fontWeight: dpsTheme.typography.fontWeight.semibold,
                            background: cat.bgColor,
                            color: cat.textColor,
                            border: `1px solid ${cat.borderColor}`
                          }}
                        >
                          {cat.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div style={{
        marginTop: dpsTheme.spacing.xl,
        padding: dpsTheme.spacing.lg,
        background: dpsTheme.colors.background.darker,
        borderRadius: dpsTheme.borderRadius.md,
        border: `1px solid ${dpsTheme.colors.border.secondary}`
      }}>
        <p style={{
          fontSize: dpsTheme.typography.fontSize.sm,
          color: dpsTheme.colors.text.muted,
          lineHeight: dpsTheme.typography.lineHeight.relaxed
        }}>
          Sélectionnez tous les équipements de sécurité actuellement installés dans votre logement.
          Cette information nous permettra de calculer votre score de sécurité et de vous proposer des recommandations personnalisées.
        </p>
      </div>
    </div>
  );
}